import React, { useState } from 'react';
import { CountLabel, GradientText, GradientTextAction, Poppins, Spacer } from 'src/common';
import GenericTable from 'src/components/GenericTable';
import { TabWrap } from 'src/components/TabsMenu';
import { AddUserModal, UserModal } from 'src/components/modals';
import styled from 'styled-components';
import { useMembersColumns } from './useMembersColumns';
import { useInvitesColumns } from './useInvitesColumns';
import { useRequestsColumns } from './useRequestsColumns';
import { useAuth } from 'src/state/auth';
import { usePlaceholdersColumns } from './usePlaceholdersColumns';
import * as Yup from 'yup';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { WorkspaceQuery } from 'src/graphql/_generated';
import { useCache } from 'src/graphql';

const Div = styled(TabWrap)`
  background-color: ${({ theme }) => theme.colors.white};

  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .header-label {
    font-weight: bold;
    font-size: 18px;
    color: ${({ theme }) => theme.colors.cflowerBlue};
  }

  .separator {
    height: 1px;
    width: 100%;
    background-color: ${({ theme }) => theme.colors.stroke};
  }
`;

type HandleOpenInviteForm = (a: { ownerId: string; text: string; email: string }) => void;

interface MembersProps {
  workspace: NonNullable<WorkspaceQuery['workspace']>;
}

export const Members: React.FC<MembersProps> = ({ workspace }) => {
  const [addUserModalOpen, setAddUserModalOpen] = useState<{ ownerId: string; name: string; email: string } | null>(
    null,
  );
  const { usersOptios } = useAuth();
  const { refetchQuery } = useCache();

  const placeholders = usersOptios.filter((el) => !el.inviteId && !el.memberId);

  const { columns, GTColumnSelectAnchorExported, profileModalId, setProfileModalId } = useMembersColumns({
    data: workspace.users,
    workspace,
  });

  const { columns: columnsRequest, GTColumnSelectAnchorExported: GTColumnSelectAnchorExportedRequest } =
    useRequestsColumns({ workspace });

  const { columns: columnsInvite, GTColumnSelectAnchorExported: GTColumnSelectAnchorExportedInvite } =
    useInvitesColumns({ workspace });

  const handleOpenInviteForm: HandleOpenInviteForm = ({ ownerId, text, email }) => {
    const isTextEmail = Yup.string().email().isValidSync(text);
    const isEmailEmail = Yup.string().email().isValidSync(email);

    const emailInput = isTextEmail ? text : isEmailEmail ? email : text;
    const nameInput = isTextEmail ? '' : text;

    setAddUserModalOpen({ ownerId, email: emailInput, name: nameInput });
  };

  const { columns: columnsPlaceholder, GTColumnSelectAnchorExported: GTColumnSelectAnchorExportedPlaceholder } =
    usePlaceholdersColumns({
      workspace,
      onInviteClick: handleOpenInviteForm,
    });

  return (
    <>
      {addUserModalOpen && (
        <AddUserModal
          id={workspace.id}
          type="workspace"
          onClose={() => setAddUserModalOpen(null)}
          emailsInUse={[...workspace.invites, ...workspace.requests, ...workspace.users].map((el) => el.email || '')}
          initialValues={addUserModalOpen}
        />
      )}
      {profileModalId && (
        <UserModal
          onClose={() => setProfileModalId(null)}
          externalUserId={profileModalId}
          workspaceId={workspace.id}
          onUserUpdated={() => refetchQuery({ include: ['workspace'] })}
          highlightRoles
        />
      )}
      <Div>
        {/* MEMBERS */}
        {workspace.actionsOnThisWorkspace?.canSeeMembers && (
          <>
            <GradientText>MEMBERS</GradientText>
            <Spacer $px={30} />
            <div className="head">
              <CountLabel label="member" length={workspace.users.length} />
              <Spacer $px={5} />
              <GradientTextAction
                onClick={() => {
                  setAddUserModalOpen({ ownerId: '', name: '', email: '' });
                  mpEvent(MPEvents.ButtonClick, {
                    button: 'Add a user',
                    tags: ['WORKSPACE'],
                  });
                }}
                weight={500}
              >
                + Add a User
              </GradientTextAction>
            </div>
            <Spacer $px={20} />
            <GenericTable
              tableId={TableIds.wsMembers}
              data={workspace.users}
              columns={columns}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
              searchable={['name', 'email', 'role']}
            />
          </>
        )}
        {/* REQUESTS */}
        {workspace.requests.length > 0 && workspace.actionsOnThisWorkspace?.canSeeRequests && (
          <>
            <Spacer $px={30} />
            <Poppins className="header-label">REQUESTS</Poppins>
            <Spacer $px={16} />
            <CountLabel label="request" length={workspace.requests.length} />
            <Spacer $px={5} />

            <GenericTable
              tableId={TableIds.wsRequests}
              data={workspace.requests}
              columns={columnsRequest}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExportedRequest}
              searchable={['email', 'name']}
            />
          </>
        )}
        {/* INVITES */}
        {workspace.invites.length > 0 && workspace.actionsOnThisWorkspace?.canSeeInvites && (
          <>
            <Spacer $px={30} />
            <Poppins className="header-label">INVITES</Poppins>
            <Spacer $px={16} />
            <CountLabel label="invite" length={workspace.invites.length} />
            <Spacer $px={5} />
            <GenericTable
              tableId={TableIds.wsInvites}
              data={workspace.invites}
              columns={columnsInvite}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExportedInvite}
              searchable={['email', 'name']}
            />
          </>
        )}
        {/* PLACEHOLDERS */}
        {placeholders.length > 0 && (
          <>
            <Spacer $px={30} />
            <Poppins className="header-label">PEOPLE</Poppins>
            <Spacer $px={16} />
            <CountLabel label="people" length={placeholders.length} noPlural />
            <Spacer $px={5} />
            <GenericTable
              tableId={TableIds.wsPeople}
              data={placeholders}
              columns={columnsPlaceholder}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExportedPlaceholder}
              searchable={['text', 'email', 'id']}
            />
          </>
        )}
      </Div>
    </>
  );
};
